import { Injectable } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { Invoice, Salesitem } from '../../../modals/invoice.model';
import { Product, ProductMapValue } from '../../../modals/product.modals';
import { FormDataService } from '../../../services/form-data.service';
import { Customer } from '../../../services/offer-group.service';
import { MobileStockReservationService } from './mobile-stock-reservation.service';

/**
 * Shared dependency bag for the product-mapping cluster (mapping raw API
 * items into cart Product rows, invoice-to-exchange loading, product
 * search/selection). `onInvoiceRowClick`/`onInvoiceSelected` have an async
 * gap (`ensureCustomersLoaded().then(...)`), so state is exposed as live
 * getters/setters rather than snapshots, consistent with the other
 * cross-module clusters in this refactor.
 *
 * Methods NOT in this module's list (removeItemByPSID, addProducts,
 * disableOfferGroupMode, updateFormDataWithQuotationMode,
 * resetPartPaymentStates, getRoundedTotal, ensureCustomersLoaded,
 * isInterStateTax, DiscountchangeCalc, focusFirstExchangeQuantityInput,
 * triggerBounce) are exposed as plain callbacks into the component's
 * existing methods.
 */
export interface ProductMappingDeps {
  getIsDeleteMode: () => boolean;
  setAutoCompletedSelectedProduct: (value: Product) => void;
  setSelecteMapping: (value: any) => void;

  getSelectedCustomer: () => Customer | null;
  setSelectedCustomer: (value: Customer | null) => void;

  getIsSuspenseMode: () => boolean;
  getIsQuotationMode: () => boolean;
  getIsWholesaleMode: () => boolean;
  setIsQuotationMode: (value: boolean) => void;
  setIsWholesaleMode: (value: boolean) => void;
  getIsOfferGroupMode: () => boolean;
  setIsSuspenseMode: (value: boolean) => void;

  getCartProducts: () => Product[];
  setCartProducts: (value: Product[]) => void;
  setProductsTemp: (value: Partial<Product[]>) => void;

  setSelectedInvoiceData: (value: Invoice) => void;
  setSelectedInvoiceNo: (value: string) => void;
  setIsExchangeOrder: (value: boolean) => void;

  getMobileNumber: () => string;
  setMobileNumber: (value: string) => void;
  setCustomerName: (value: string) => void;
  getFormData: () => any;

  setSelectedPaymentOptions: (value: string[]) => void;
  setRefundCashAmount: (value: number) => void;
  setDiscountMode: (value: 'percentage' | 'amount') => void;
  getCustomers: () => Customer[];
  getIsSalesDiscountEditEnabled: () => boolean;

  setShowBillDetail: (value: boolean) => void;
  setShowItemDetail: (value: boolean) => void;
  setDisplayNoItem: (value: boolean) => void;
  setDisplayTransactions: (value: boolean) => void;
  setActiveItem: (value: any) => void;
  getItems: () => MenuItem[] | undefined;
  getEnableCursorFocus: () => boolean;

  setShowInvoiceDialog: (value: boolean) => void;

  productSearch$: Subject<string | undefined>;

  setFilteredProducts: (value: Product[]) => void;
  getIsProductSearch: () => boolean;
  setIsProductSearch: (value: boolean) => void;

  setSelectedProductId: (value: string | null) => void;
  setCurrentPSID: (value: string) => void;
  setShowCalculator: (value: boolean) => void;

  removeItemByPSID: (psid: string) => void;
  addProducts: (psid: string, sellingprice?: number, batch?: string) => void;
  disableOfferGroupMode: () => void;
  updateFormDataWithQuotationMode: () => void;
  resetPartPaymentStates: () => void;
  getRoundedTotal: () => number;
  ensureCustomersLoaded: () => Promise<void>;
  isInterStateTax: () => boolean;
  discountChangeCalc: () => void;
  focusFirstExchangeQuantityInput: () => void;
  triggerBounce: () => void;
}

/**
 * ProductMappingService
 *
 * Dedicated service for mapping raw API items (sales items, quotation
 * items, resumed-receipt items) into cart Product rows, loading an
 * existing invoice's items for exchange, and product search/selection
 * handling (autocomplete, dropdown, barcode-scan add/remove routing).
 *
 * Extracted from DashboardComponent to decouple product-mapping logic
 * while preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductMappingService {
  constructor(
    private readonly messageService: MessageService,
    private readonly formDataService: FormDataService,
    private readonly mobileStockReservationService: MobileStockReservationService,
  ) {}

  dropdownSelection(event: any, deps: ProductMappingDeps) {
    if (deps.getIsDeleteMode()) {
      // In Delete Mode: Remove item from cart
      deps.removeItemByPSID(event.option.value);
    } else {
      // Normal Mode: Add item to cart
      deps.addProducts(event.option.value);
    }
    deps.setAutoCompletedSelectedProduct(null as any);
  }

  onMappingSelected(product: any, event: any, deps: ProductMappingDeps) {
    const newMappingId = event.value;

    if (product) {
      product.selectedMappingId = newMappingId;
      product.mapping_id = newMappingId;
    }
    deps.setSelecteMapping(newMappingId);

    // -----------------------------------------------------------------------
    // Post-mapping duplicate detection
    // Now that the user has chosen a mapping we have the full composite key:
    //   psid + variantId + mappingId
    // If another cart row already carries the exact same triple, merge or
    // remove this row (same logic as onVariantSelected for serialized units).
    // -----------------------------------------------------------------------
    if (!product || newMappingId == null || !product.selectedMapValue) return;

    const cart: Product[] = deps.getCartProducts();
    const duplicateIndex = cart.findIndex(
      (item) =>
        item !== product &&
        item.psid?.toLowerCase() === product.psid?.toLowerCase() &&
        item.selectedMapValue?.id === product.selectedMapValue!.id &&
        (item.mapping_id ?? item.selectedMappingId ?? null) === newMappingId,
    );

    if (duplicateIndex === -1) {
      // No duplicate — nothing to merge.
      deps.setProductsTemp(cart.map((p) => ({ ...p })));
      return;
    }

    const existing = cart[duplicateIndex];
    const currentIndex = cart.indexOf(product);
    const isSerialized = !!product.selectedMapValue?.parameter1_value;

    if (isSerialized) {
      // Same IMEI/serial cannot appear twice — collapse to the existing row.
      if (currentIndex !== -1) cart.splice(currentIndex, 1);
      deps.setCartProducts([...cart]);
      deps.setProductsTemp(cart.map((p) => ({ ...p })));
      this.mobileStockReservationService.syncToStorage(cart);
      this.messageService.add({
        severity: 'warn',
        summary: 'Already in Cart',
        detail: `${product.name} (${product.selectedMapValue?.parameter1_value}) is already in the cart — duplicate removed.`,
        life: 3000,
      });
    } else {
      // Non-serialized: merge quantities, capped at what the shared
      // batch/variant pool actually has left — other mapping rows drawing
      // from the same productmapvalues entry (e.g. a different phone model
      // tag on the same batch of cases) must not be double-counted, and the
      // two rows being merged here must not count against themselves.
      const poolKey = this.mobileStockReservationService.buildPoolKey(
        product.psid,
        product.selectedMapValue?.id,
      );
      const maxQty = this.mobileStockReservationService.getAvailableQty(
        poolKey,
        product.selectedMapValue?.qty ?? Infinity,
        cart,
        [product, existing],
      );
      const mergedQty = Math.min(
        (existing.increaseQuantity || 0) + (product.increaseQuantity || 1),
        maxQty,
      );
      existing.increaseQuantity = mergedQty;
      if (currentIndex !== -1) cart.splice(currentIndex, 1);
      deps.setCartProducts([...cart]);
      deps.setProductsTemp(cart.map((p) => ({ ...p })));
      this.mobileStockReservationService.syncToStorage(cart);
      this.messageService.add({
        severity: 'success',
        summary: 'Merged',
        detail: `Combined with existing ${product.name} row — quantity now ${mergedQty}.`,
        life: 3000,
      });
    }
  }

  // Reusable method to map items to Product structure
  mapItemsToProducts(
    items: any[],
    isExchangeOrder: boolean = false,
    isQuotation: boolean = false,
    deps: ProductMappingDeps,
  ): Product[] {
    console.log('Mapping items to products with isExchangeOrder=' + items);
    return items.map((item) => {
      let basePrice = item.price || item.sellingprice;
      const discountAmt = item.discountamt || item.discountval || 0;

      if (isQuotation) {
        // Saved quotation lines come back with their list price (originalprice) as
        // barcodesellprice. Prefer it: the saved price is already gross for quotations
        // created through placeorder, so adding the discount again would inflate it.
        const savedListPrice = Number(item.barcodesellprice) || 0;
        if (savedListPrice > 0) {
          basePrice = savedListPrice;
        } else if (discountAmt > 0) {
          basePrice = (item.price || item.sellingprice) + discountAmt;
        }
      }

      let ctax = item.ctax;
      let stax = item.stax;
      let itax = item.itax || 0;

      if (isExchangeOrder && deps.getSelectedCustomer()) {
        if (deps.isInterStateTax()) {
          // Inter-state: Use ONLY itax (IGST)
          if (!itax || itax === 0) {
            itax = Number(item.ctax || 0) + Number(item.stax || 0);
          }
          ctax = 0;
          stax = 0;
        } else {
          if ((!ctax || ctax === 0) && (!stax || stax === 0) && itax > 0) {
            ctax = itax / 2;
            stax = itax / 2;
          }
          itax = 0;
        }
      }

      // Extract mapping details (handle string/number ID, array, or object)
      let initialMappingId: number | string | null =
        item.selectedMappingId ?? item.mapping_id ?? null;
      if (initialMappingId == null && item.mapping) {
        if (typeof item.mapping === 'object') {
          initialMappingId =
            item.mapping.mid ??
            item.mapping.id ??
            item.mapping.mapping_id ??
            null;
        } else {
          initialMappingId = item.mapping;
        }
      }

      let mappingsList: any[] = [];
      if (Array.isArray(item.mappings) && item.mappings.length > 0) {
        mappingsList = item.mappings.map((m: any) => ({
          id: m.id ?? m.mid ?? m.mapping_id,
          name: m.name ?? m.mapping_name ?? '',
          mid: m.mid ?? m.id ?? m.mapping_id,
        }));
      } else if (item.mapping && typeof item.mapping === 'object') {
        const mId =
          item.mapping.mid ?? item.mapping.id ?? item.mapping.mapping_id;
        const mName = item.mapping.name ?? item.mapping.mapping_name ?? '';
        if (mId != null) {
          mappingsList = [{ id: mId, mid: mId, name: mName }];
        }
      }

      // Extract features / selected variant details from sales return item
      let selectedMapValue: ProductMapValue | null =
        item.selectedMapValue ?? null;
      if (!selectedMapValue && item.features) {
        const f = item.features;
        const vList = f.variants || [];
        const pList = f.parameters || [];
        const v1 = vList.length > 0 ? vList[0] : null;
        const v2 = vList.length > 1 ? vList[1] : null;
        const p1 = pList.length > 0 ? pList[0] : null;
        const p2 = pList.length > 1 ? pList[1] : null;

        const val1Name = v1
          ? (v1.variant_val_name ??
            v1.variant_value_name ??
            v1.variant_value ??
            null)
          : null;
        const val2Name = v2
          ? (v2.variant_val_name ??
            v2.variant_value_name ??
            v2.variant_value ??
            null)
          : null;
        const param1Name = p1
          ? (p1.parameter_val_name ??
            p1.parameter_value_name ??
            p1.parameter_value ??
            null)
          : null;
        const param2Name = p2
          ? (p2.parameter_val_name ??
            p2.parameter_value_name ??
            p2.parameter_value ??
            null)
          : null;

        selectedMapValue = {
          id: f.productmapvalue_id || f.id || 0,
          product_id: String(item.product_id || item.productId || ''),
          qty: item.available_qty ?? f.qty ?? 0,
          variant1_id: v1 ? (v1.variant_id ?? null) : null,
          variantval1_id: v1
            ? (v1.variant_val_id ?? v1.variant_value_id ?? null)
            : null,
          variant1_value: val1Name ? String(val1Name).trim() : null,
          variant2_id: v2 ? (v2.variant_id ?? null) : null,
          variantval2_id: v2
            ? (v2.variant_val_id ?? v2.variant_value_id ?? null)
            : null,
          variant2_value: val2Name ? String(val2Name).trim() : null,
          parameter1_id: p1 ? (p1.parameter_id ?? null) : null,
          parameter1_value: param1Name ? String(param1Name).trim() : null,
          parameter2_id: p2 ? (p2.parameter_id ?? null) : null,
          parameter2_value: param2Name ? String(param2Name).trim() : null,
          parameter3_id: null,
          parameter3_value: null,
          parameter4_id: null,
          parameter4_value: null,
          purchaseorder_id: f.purchaseorder_id || item.purchaseorder_id || 0,
          purchaseorderitem_id:
            f.purchaseorderitem_id || item.purchaseorderitem_id || 0,
          status: f.status || 'Active',
          batch: item.batch || f.batch || '',
          variants: vList,
          parameters: pList,
        };
      }
      console.log(item, 'get from api?');
      // Display-only incl-tax unit price for returned/exchange rows — sales history
      // items never carry this field, so the cart's "Unit Price (Incl. Tax)" input
      // would otherwise render empty. Underlying sellingprice/basePrice stay exclusive
      // and drive subtotal/net-value exactly as before.
      const inclTaxRatePct =
        (Number(ctax) || 0) + (Number(stax) || 0) + (Number(itax) || 0);
      const exclUnitPriceForIncl = item.price || item.sellingprice || 0;
      const computedInclUnitPrice = inclTaxRatePct > 0
        ? Number((exclUnitPriceForIncl * (1 + inclTaxRatePct / 100)).toFixed(2))
        : Number(Number(exclUnitPriceForIncl).toFixed(2));
      const unitMappings = item.unit_mappings || item.product?.unit_mappings || [];
      const selectedUnitId =
        item.unit_id ?? item.unit ?? item.product?.unit ?? unitMappings[0]?.unitid ?? null;
      const selectedUnitMapping = unitMappings.find((mapping: any) =>
        mapping.unitid == selectedUnitId,
      );

      console.log(item, 'item barcode');
      const mappedProduct: Product = {
        product_id: item.product_id,
        sku: item.sku || item.skuid || item.barcode,
        barcode : item.product?.barcode || item.barcode,
        productvariant_id: item.productvariant_id ?? item.product?.productvariant_id ?? null,
        cost_price_incl: item.cost_price_incl ?? item.product?.cost_price_incl,
        cost_price_excl: item.cost_price_excl ?? item.product?.cost_price_excl,
        margin: item.margin ?? item.product?.margin,
        margin_value: item.margin_value ?? item.product?.margin_value,
        price: item.price || item.sellingprice,
        psid: item.psid,
        uuid: item.uuid,
        status: item.status,
        hsncode: String(item.hsncode || ''),
        ctax: ctax,
        stax: stax,
        itax: itax,
        cess: item.cess || 0,
        discount: item.discount || 0,
        discountval: item.discountamt || item.discountval || 0,
        quantity: item.quantity,
        skuid: item.sku || item.skuid || item.barcode,
        buyingprice: item.cost_price_excl ?? item.product?.cost_price_excl ?? item.costprice ?? 0,
        buyingmrp: item.mrp,
        barcodesellprice: basePrice,
        orgprice: item.orgprice || item.price || item.sellingprice,
        sellingprice: Number(Number(item.price || item.sellingprice || 0).toFixed(2)),
        sellingpriceincltax: item.sellingpriceincltax ?? item.selling_price_inc_tax ?? item.price ?? item.sellingprice,
        product_selling_price_incl: (item.product_selling_price_incl || item.selling_price_incl) != null ? Number(Number(item.product_selling_price_incl || item.selling_price_incl).toFixed(2)) : computedInclUnitPrice,
        sellingmrp: item.mrp,
        offerid: item.offerid || 0,
        offerprice: item.offerprice || item.price || item.sellingprice,
        percentage: item.taxpercentage || item.sales_tax || 0,
        productId: String(item.productId || item.product_id || ''),
        batch: item.batch || item.batchid || '',
        purchaseorderitem_id: item.purchaseorderitem_id || 0,
        name: item.name,
        tax: item.taxpercentage || item.sales_tax || item.tax || 0,
        increaseQuantity: parseFloat(item.quantity.toFixed(2)) || 1,
        exchangeQuantity: isExchangeOrder ? 0 : undefined,
        selected: undefined,
        returned: isExchangeOrder ? true : undefined,
        // New required properties
        purchaseorder_id: item.purchaseorder_id || 0,
        store_id: item.store_id || 0,
        mrp: item.mrp || 0,
        costprice: item.cost_price_excl ?? item.product?.cost_price_excl ?? item.costprice ?? 0,
        product_cost_price_incl: item.cost_price_incl ?? item.product?.cost_price_incl ?? item.product_cost_price_incl,
        product_cost_price_excl: item.cost_price_excl ?? item.product?.cost_price_excl ?? item.product_cost_price_excl,
        marginamount: item.margin_value ?? item.product?.margin_value ?? item.marginamount,
        wholesaleprice: item.wholesaleprice || 0,
        groupingprice: item.groupingprice || 0,
        retailprice: item.retailprice || 0,
        sales_tax: item.sales_tax || 0,
        rack: item.rack || '',
        discaftertax: item.discaftertax == null ? 0 : item.discaftertax,
        globalDiscountShare: item.globaldiscountshare || 0,
        // Invoice search overwrites quantity with the qty still returnable; sold_qty is the
        // original line qty that globaldiscountshare was split over.
        soldQuantity: Number(item.sold_qty) || undefined,
        isPendingVariant: false,
        isFree:
          Number(item.isFree ?? item.is_free ?? item.isfree ?? 0) === 1 ||
          item.isFree === true ||
          item.is_free === true ||
          item.isfree === true,
        is_free:
          Number(item.isFree ?? item.is_free ?? item.isfree ?? 0) === 1 ||
          item.isFree === true ||
          item.is_free === true ||
          item.isfree === true
            ? 1
            : 0,
        isfree:
          Number(item.isFree ?? item.is_free ?? item.isfree ?? 0) === 1 ||
          item.isFree === true ||
          item.is_free === true ||
          item.isfree === true
            ? 1
            : 0,
        mappings: mappingsList,
        selectedMappingId: initialMappingId,
        mapping_id: initialMappingId,
        selectedMapValue: selectedMapValue,
        unit_id: selectedUnitId,
        isbulk: item.isbulk ?? item.product?.isbulk ?? 0,
        unit: item.unit ?? item.product?.unit ?? null,
        unit_mappings: unitMappings,
        // Base-unit price/MRP sent with saved quotation lines, so a secondary unit can be re-priced
        baseunitprice: item.baseunitprice ?? undefined,
        baseunitmrp: item.baseunitmrp ?? undefined,
        uom: item.uom || selectedUnitMapping?.unitdetails?.displayname || selectedUnitMapping?.unitdetails?.name || null,
        free_qty_available: Number(item.free_qty_available ?? item.free_qty ?? 0) || 0,
        cart_free_qty: Number(item.cart_free_qty ?? item.free_qty ?? 0) || 0,
        free_qty: Number(item.free_qty ?? item.free_qty_available ?? 0) || 0,
      };
      console.log(
        'Mapped Product quantity:',
        parseFloat(item.quantity.toFixed(2)) < 1
          ? parseFloat(item.quantity.toFixed(2))
          : 1,
      );
      if (deps.getIsSuspenseMode()) {
        mappedProduct.isgst =
          item.isgst !== undefined && item.isgst !== null ? item.isgst : 1;
        mappedProduct.selected = item.isgst === 0;
      }

      if (deps.getIsQuotationMode() || isQuotation) {
        mappedProduct.stockquanity = item.stockquanity || -1;
      }

      return mappedProduct;
    });
  }

  onInvoiceRowClick(event: any, deps: ProductMappingDeps) {
    // Check if invoice has already been exchanged
    // Check if all sales items have quantity 0
    const SelectedSalesItems = event.invoice.salesitems || [];
    console.log(SelectedSalesItems);
    const allItemsZeroQuantity = SelectedSalesItems.every(
      (item: Salesitem) => !item.quantity || item.quantity === 0,
    );

    if (allItemsZeroQuantity && SelectedSalesItems.length > 0) {
      // this.hideDialog();
      this.messageService.add({
        severity: 'warn',
        summary: 'Exchange Not Allowed',
        detail:
          'This invoice has already been exchanged and cannot be exchanged again.',
        life: 3000,
      });
      return;
    }

    deps.setSelectedInvoiceData(event);
    // this.selectedInvoiceForDialog = event; // Set radio button selection
    deps.setSelectedInvoiceNo(event.invoice.invoiceno); // Store the invoice number
    deps.setIsExchangeOrder(true); // Enable exchange mode and disable product search

    // Disable all other modes when entering exchange mode
    if (deps.getIsQuotationMode()) {
      deps.setIsQuotationMode(false);
    }
    if (deps.getIsWholesaleMode()) {
      deps.setIsWholesaleMode(false);
    }
    if (deps.getIsOfferGroupMode()) {
      deps.disableOfferGroupMode();
    }
    if (deps.getIsSuspenseMode()) {
      deps.setIsSuspenseMode(false);
      deps.getCartProducts().forEach((product) => {
        delete product.isgst;
        product.selected = false;
      });
      deps.setProductsTemp(
        deps.getCartProducts().map((product) => ({ ...product })),
      );
    }

    deps.updateFormDataWithQuotationMode();

    deps.setMobileNumber(event.invoice.user_id + '');
    deps.setCustomerName(event.invoice.name);

    const formData = deps.getFormData();
    this.formDataService.updateFormData({
      ...formData,
      mobileNumber: deps.getMobileNumber(),
      customerName: event.invoice.name,
      empCode: formData?.empCode || '',
      storeCode: formData?.storeCode || '',
      currentDate: formData?.currentDate || new Date(),
      salesRep: formData?.salesRep || '',
      isExchangeOrder: true,
    });

    // Reset part payment states for exchange orders
    deps.resetPartPaymentStates();

    // Set payment options for exchange order - always include Cash
    deps.setSelectedPaymentOptions(['Cash']);

    // Initialize refund cash amount for exchange order
    deps.setRefundCashAmount(deps.getRoundedTotal()); // Start with exchange total

    let discflag = 0;
    SelectedSalesItems.forEach((item: Salesitem) => {
      if (item.discount > 0 && item.discountamt !== item.discount) {
        discflag = 1;
      }
    });
    deps.setDiscountMode(discflag === 1 ? 'percentage' : 'amount');

    // Load customer data to get state information for tax calculation
    // This must happen BEFORE mapping items so tax can be recalculated correctly
    deps.ensureCustomersLoaded().then(() => {
      const customer = deps
        .getCustomers()
        .find((c) => c.mobile_no === deps.getMobileNumber());
      if (customer) {
        deps.setSelectedCustomer(customer);
      } else {
        deps.setSelectedCustomer(null);
      }

      // Map items AFTER customer is loaded so tax can be recalculated
      deps.setCartProducts(
        this.mapItemsToProducts(SelectedSalesItems, true, false, deps),
      );

      // Only recalculate if user can edit discounts
      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }
      deps.setProductsTemp(
        deps.getCartProducts().map((product) => ({ ...product })),
      );
    });

    // Ensure section 3 (bill detail) is visible for exchange items
    deps.setShowBillDetail(true);
    deps.setShowItemDetail(false);
    deps.setDisplayNoItem(false);
    deps.setDisplayTransactions(false);
    deps.setActiveItem(deps.getItems() && deps.getItems()![0]); // Set active tab to show bill details

    // this.hideDialog();

    if (deps.getEnableCursorFocus()) {
      setTimeout(() => {
        deps.focusFirstExchangeQuantityInput();
      }, 200);
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Exchange Items Loaded',
      detail: `${deps.getCartProducts().length} items loaded for exchange`,
      life: 3000,
    });
  }

  mapSalesItemToProduct(
    salesItem: Salesitem,
    products: Product[],
  ): Product | undefined {
    return products.find(
      (product) =>
        product.product_id === salesItem.product_id &&
        product.hsncode === salesItem.hsncode.toString() &&
        product.psid?.toLowerCase() === salesItem.psid?.toLowerCase(),
    );
  }

  // Handler for invoice selection from the reusable dialog component
  onInvoiceSelected(event: any, deps: ProductMappingDeps) {
    // event is InvoiceSelectionEvent — the selected invoice is at event.invoice
    const invoiceData = event.invoice;

    // --- 1. Validate: reject invoices already fully exchanged ---
    const SelectedSalesItems = invoiceData.invoice.salesitems || [];
    console.log(SelectedSalesItems);

    // --- 2. Set selected invoice state ---
    deps.setSelectedInvoiceData(invoiceData);
    deps.setSelectedInvoiceNo(invoiceData.invoice.invoiceno);
    deps.setIsExchangeOrder(true);

    // --- 3. Disable all other modes ---
    if (deps.getIsQuotationMode()) {
      deps.setIsQuotationMode(false);
    }
    if (deps.getIsWholesaleMode()) {
      deps.setIsWholesaleMode(false);
    }
    if (deps.getIsOfferGroupMode()) {
      deps.disableOfferGroupMode();
    }
    if (deps.getIsSuspenseMode()) {
      deps.setIsSuspenseMode(false);
      deps.getCartProducts().forEach((product) => {
        delete product.isgst;
        product.selected = false;
      });
      deps.setProductsTemp(
        deps.getCartProducts().map((product) => ({ ...product })),
      );
    }

    deps.updateFormDataWithQuotationMode();

    // --- 4. Update customer info from the invoice ---
    deps.setMobileNumber(invoiceData.invoice.user_id + '');
    deps.setCustomerName(invoiceData.invoice.name);

    const formData = deps.getFormData();
    this.formDataService.updateFormData({
      ...formData,
      mobileNumber: deps.getMobileNumber(),
      customerName: invoiceData.invoice.name,
      empCode: formData?.empCode || '',
      storeCode: formData?.storeCode || '',
      currentDate: formData?.currentDate || new Date(),
      salesRep: formData?.salesRep || '',
      isExchangeOrder: true,
    });

    // --- 5. Reset payment states & set exchange defaults ---
    deps.resetPartPaymentStates();
    deps.setSelectedPaymentOptions(['Cash']);
    deps.setRefundCashAmount(deps.getRoundedTotal());

    // --- 6. Detect discount mode from sales items ---
    let discflag = 0;
    SelectedSalesItems.forEach((item: Salesitem) => {
      if (item.discount > 0 && item.discountamt !== item.discount) {
        discflag = 1;
      }
    });
    deps.setDiscountMode(discflag === 1 ? 'percentage' : 'amount');

    // --- 7. Load customer for tax state, then map products ---
    // Must load customer BEFORE mapping so inter/intra state tax is correct
    deps.ensureCustomersLoaded().then(() => {
      const customer = deps
        .getCustomers()
        .find((c) => c.mobile_no === deps.getMobileNumber());
      deps.setSelectedCustomer(customer ?? null);

      // Map invoice sales items to the products cart array
      deps.setCartProducts(
        this.mapItemsToProducts(SelectedSalesItems, true, false, deps),
      );

      // Recalculate discounts if the user has edit permission
      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }

      deps.setProductsTemp(
        deps.getCartProducts().map((product) => ({ ...product })),
      );

      // Show success after products are ready (count is now accurate)
      this.messageService.add({
        severity: 'success',
        summary: 'Exchange Items Loaded',
        detail: `${deps.getCartProducts().length} items loaded for exchange`,
        life: 3000,
      });
    });

    // --- 8. Update UI state to show bill detail view ---
    deps.setShowBillDetail(true);
    deps.setShowItemDetail(false);
    deps.setDisplayNoItem(false);
    deps.setDisplayTransactions(false);
    deps.setActiveItem(deps.getItems() && deps.getItems()![0]);

    // --- 9. Focus first exchange quantity input ---
    if (deps.getEnableCursorFocus()) {
      setTimeout(() => {
        deps.focusFirstExchangeQuantityInput();
      }, 200);
    }
  }

  // Handler for dialog close event
  onInvoiceDialogClosed(deps: ProductMappingDeps) {
    deps.setShowInvoiceDialog(false);
  }

  getProducts(searchText: string | undefined, deps: ProductMappingDeps) {
    deps.productSearch$.next(searchText);
  }

  formAllProducts(
    products: Product[],
    isResumeReceipt: boolean | undefined,
    deps: ProductMappingDeps,
  ) {
    if (products?.length) {
      const productList = products || [];
      const productsTemp = productList.map((product) => {
        this.normalizeProductColumns(product);

        if (deps.getIsQuotationMode()) {
          // In quotation mode, always use barcodesellprice
          product.sellingprice = product.barcodesellprice;
        } else if (
          deps.getIsOfferGroupMode() &&
          product.groupingprice &&
          product.groupingprice > 0
        ) {
          product.sellingprice = product.groupingprice;
        } else if (product.offerprice > 0) {
          product.sellingprice = product.offerprice;
        }

        const quantity = isResumeReceipt
          ? product.quantity || product.increaseQuantity || 1
          : 1;

        // Determine discount values based on context and settings
        let discountValue = 0;
        let discountValValue = 0;

        if (isResumeReceipt) {
          // Resuming hold/quotation: Use saved discount values (backend already calculated)
          discountValue = product.discount || 0;
          discountValValue = product.discountval || 0;
        } else if (!deps.getIsSalesDiscountEditEnabled()) {
          // New product display with setting disabled: Use API discount values as-is
          discountValue = product.discount || 0;
          discountValValue = product.discountval || 0;
          // Note: API's sellingprice already has discount applied, don't recalculate
        }
        // else: New product with setting enabled: Start with 0 (user controls)

        // A single available mapping is selected automatically; the user is
        // only asked to choose when there are several.
        const singleMappingId =
          product.mappings?.length === 1
            ? (product.mappings[0].id ?? null)
            : null;
        const initialMappingId =
          product.selectedMappingId ?? product.mapping_id ?? singleMappingId;

        const isFreeVal = Number(product.isFree ?? product.is_free ?? (product as any).isfree ?? 0) === 1 || product.isFree === true || product.is_free === true || (product as any).isfree === true;
        const unitId = product.unit_id ?? product.unit ?? product.unit_mappings?.[0]?.unitid ?? null;
        const defaultUnitId = unitId != null ? Number(unitId) : null;
        let formattedProduct = {
          ...product,
          unit_id: defaultUnitId,
          isFree: isFreeVal,
          is_free: isFreeVal ? 1 : 0,
          isfree: isFreeVal ? 1 : 0,
          mappings: product.mappings || [],
          selectedMappingId: initialMappingId,
          mapping_id: initialMappingId,
          increaseQuantity: quantity,
          discount: discountValue,
          discountval: discountValValue,
          free_qty_available:
            Number(
              product.free_qty_available ?? (product as any).free_qty ?? 0,
            ) || 0,
          cart_free_qty: Number(product.cart_free_qty ?? 0) || 0,
        };

        // No price recalculation needed - API already provides correct sellingprice

        // Only set default isgst/selected if NOT resuming receipt
        // When resuming, preserve the saved values from backend
        if (deps.getIsSuspenseMode() && !isResumeReceipt) {
          formattedProduct.isgst = 1;
          formattedProduct.selected = false;
        }

        return formattedProduct;
      });
      deps.setProductsTemp(productsTemp);
      if (isResumeReceipt) {
        deps.setCartProducts(productsTemp?.slice() as any);
      }
      deps.setFilteredProducts(productsTemp?.slice() as any);
    } else {
      deps.setFilteredProducts([]);
    }
  }

  normalizeProductColumns(product: Product): void {
    product.product_cost_price_incl =
      product.cost_price_incl ?? product.product_cost_price_incl;
    product.product_cost_price_excl =
      product.cost_price_excl ?? product.product_cost_price_excl;
    product.costprice = product.cost_price_excl ?? product.costprice;
    product.buyingprice = product.cost_price_excl ?? product.buyingprice;
    product.marginamount = product.margin_value ?? product.marginamount;
  }

  /** A product with exactly one mapping gets it selected; several → user picks. */
  autoSelectSingleMapping(product: Product): void {
    if (product.mappings?.length !== 1) return;
    if ((product.selectedMappingId ?? product.mapping_id) != null) return;
    const id = product.mappings[0].id ?? null;
    product.selectedMappingId = id;
    product.mapping_id = id;
  }

  populateFilterProducts(event: any, deps: ProductMappingDeps) {
    let query = event.query ?? event;
    if (typeof event === 'object') return;

    if (!query || query.trim().length < 1) {
      deps.setFilteredProducts([]);
      return;
    }

    deps.setActiveItem(deps.getItems() && deps.getItems()![0]);
    deps.setDisplayNoItem(false);

    deps.setShowBillDetail(true);
    deps.setShowItemDetail(false);
    deps.setIsProductSearch(true);
    if (deps.getIsProductSearch()) {
      deps.setFilteredProducts([]);
      this.getProducts(query, deps);
    } else {
      if (deps.getIsDeleteMode()) {
        // In Delete Mode: Remove item from cart
        deps.removeItemByPSID(query);
      } else {
        // Normal Mode: Add item to cart
        deps.addProducts(query);
      }
    }
  }

  onSelectProduct(
    productId: string | null,
    psid: string,
    isUISelect: boolean | undefined,
    deps: ProductMappingDeps,
  ) {
    let getProduct = deps
      .getCartProducts()
      .find(
        (selectproduct) =>
          selectproduct.psid?.toLowerCase() === psid?.toLowerCase(),
      );
    deps.setSelectedProductId(
      getProduct?.productId || getProduct?.product_id || (productId as any),
    );
    deps.setCurrentPSID(psid);
    if (isUISelect) {
      deps.setActiveItem(deps.getItems() && deps.getItems()![1]);
      deps.setDisplayNoItem(false);
      deps.setShowCalculator(false);
      deps.setShowBillDetail(false);
    } else {
      deps.setActiveItem(deps.getItems() && deps.getItems()![0]);
      deps.setShowBillDetail(true);
    }

    deps.triggerBounce();
    const selectedProductId =
      getProduct?.productId || getProduct?.product_id || (productId as any);
    if (!selectedProductId) {
      deps.setDisplayNoItem(true);
      deps.setDisplayTransactions(false);
    } else {
      deps.setDisplayNoItem(false);
      if (isUISelect) deps.setShowItemDetail(true);
      else deps.setShowItemDetail(false);
      deps.setDisplayTransactions(false);
    }
  }

  findSelectedProduct(
    productId: string,
    deps: ProductMappingDeps,
  ): Product | null {
    // console.log(deps.getCartProducts(),'deps.getCartProducts()');
    return (
      deps
        .getCartProducts()
        .find((detail) => detail?.productId === productId) || null
    );
  }

  trackByProductId(index: number, product: Product): string {
    // Mobile industry: two cart rows can share the same productId (same
    // product+batch+variant sold under different mappings, e.g. "S2" vs
    // "A36"). A bare productId key would make Angular's *ngFor diffing
    // conflate those rows on add/remove, so fold in the row's variant +
    // mapping + index to keep the key unique per row.
    const variantPart = product?.selectedMapValue?.id ?? '';
    const mappingPart = product?.mapping_id ?? product?.selectedMappingId ?? '';
    return `${product?.productId}::${product?.psid ?? ''}::${variantPart}::${mappingPart}::${index}`;
  }
}
